import { LitElement, html, css, unsafeCSS } from 'lit';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import styl from './TopBar.scss?raw';

export class TopBar extends LitElement {
  static properties = {
    logo: { type: String },
    onLogoClick: { type: Function },

    // Flexible items array: each item can be a button, group, device-switcher, divider, or spacer
    // Item types: 'button', 'group', 'device-switcher', 'divider', 'spacer'
    items: { type: Array },

    // Active device for device switchers
    activeDevice: { type: String },

    // Enable/disable responsive overflow menu
    enableOverflow: { type: Boolean },

    // Internal state for overflow menu
    _visibleItems: { type: Array, state: true },
    _overflowItems: { type: Array, state: true },
    _showOverflowMenu: { type: Boolean, state: true },
  };

  createRenderRoot() {
    return this; // Disable shadow DOM
  }

  constructor() {
    super();
    this.logo = '';
    this.onLogoClick = () => {};
    this.items = [];
    this.activeDevice = 'desktop';
    this.enableOverflow = true; // Enabled by default
    this._visibleItems = []; //not needed
    this._overflowItems = []; // not needed
    this._showOverflowMenu = false; // not needed, clear it carefully
    this._resizeObserver = null;
    this._isCheckingOverflow = false; // not needed
    this._lastContainerWidth = 0; // not needed
  }

  connectedCallback() {
    super.connectedCallback();
    // Use window resize instead of ResizeObserver for better stability
    this._setupWindowResize();

    // Only listen for clicks on this component specifically
    this.addEventListener('click', this._handleTopbarClick);

    // Listen for clicks outside to close menu
    document.addEventListener('mousedown', this._handleOutsideClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this._resizeTimeout) {
      clearTimeout(this._resizeTimeout);
    }
    window.removeEventListener('resize', this._handleWindowResize);
    this.removeEventListener('click', this._handleTopbarClick);
    document.removeEventListener('mousedown', this._handleOutsideClick);
  }
  updated(changedProperties) {
    super.updated(changedProperties);
    if (changedProperties.has('items')) {
      // First, set all items as visible so they render
      if (!this._visibleItems || this._visibleItems.length === 0) {
        this._visibleItems = [...this.items];
        this._overflowItems = [];
      }

      // Then check overflow after render
      requestAnimationFrame(() => {
        // Double RAF to ensure DOM is fully rendered
        requestAnimationFrame(() => {
          this._updateCarousel();
        });
      });
    }
  }

  firstUpdated() {
    super.firstUpdated();
    // Ensure items are visible initially
    if (this.items.length > 0 && this._visibleItems.length === 0) {
      this._visibleItems = [...this.items];
    }

    // Check overflow on initial render
    requestAnimationFrame(() => {
      // Double RAF to ensure DOM is fully rendered
      requestAnimationFrame(() => {
        this._updateCarousel();
      });
    });
  }

  _setupWindowResize() {
    this._updateCarousel();
    // Bind the handler so we can remove it later
    this._handleWindowResize = () => {
      // Clear any pending timeout to throttle resize events
      if (this._resizeTimeout) {
        clearTimeout(this._resizeTimeout);
      }

      // Throttle resize events - check after user stops resizing for 200ms
      this._resizeTimeout = setTimeout(() => {
        if (!this.enableOverflow || this._isCheckingOverflow) {
          // return;
        }

        // Use requestAnimationFrame for better performance
        requestAnimationFrame(() => {
          this._updateCarousel();
        });
      }, 200); // 200ms throttle - responsive but not too aggressive
    };

    // Listen to window resize with passive option for better scroll performance
    window.addEventListener('resize', this._handleWindowResize, { passive: true });
  }
  _updateCarousel() {
    if (!this.enableOverflow) return;
    const container = this.querySelector('.toolbar-content');
    if (!container) return;
    let isScroll = this.visuallyOverflows(container);
    let leftArrow = this.querySelector('.lftCroller');
    let rightArrow = this.querySelector('.rgtCroller');

    // Check if the container is overflowing
    if (isScroll) {
      leftArrow.style.display = 'flex';
      rightArrow.style.display = 'flex';
      //console.log('Carousel is overflowing');
    } else {
      leftArrow.style.display = 'none';
      rightArrow.style.display = 'none';
      //console.log('Carousel is not overflowing');
    }
  }
  visuallyOverflows(el, eps = 0.5) {
    const c = el.getBoundingClientRect();
    for (const child of el.children) {
      const r = child.getBoundingClientRect();
      if (r.left < c.left - eps || r.right > c.right + eps) return true;
    }
    return false;
  }

  _handleTopbarClick = (e) => {
    // Handle clicks within the topbar component only
    const button = this.querySelector('.more-menu-button');
    const menu = this.querySelector('.overflow-menu');

    // If clicking the more button, let _toggleOverflowMenu handle it
    if (button && button.contains(e.target)) {
      return;
    }

    // If clicking inside menu, let it handle normally
    if (menu && menu.contains(e.target)) {
      return;
    }

    // Any other click in topbar should close the menu if it's open
    if (this._showOverflowMenu) {
      this._showOverflowMenu = false;
      this.requestUpdate();
      e.stopPropagation();
    }
  };

  _handleOutsideClick = (e) => {
    // Close overflow menu when clicking outside the topbar
    if (!this._showOverflowMenu) return;

    if (!this.contains(e.target)) {
      this._showOverflowMenu = false;
      this.requestUpdate();
    }
  };

  _toggleOverflowMenu() {
    this._showOverflowMenu = !this._showOverflowMenu;
    this.requestUpdate();
  }

  /**
   * Check if an item or its children have notifications
   * @private
   */
  _itemHasNotification(item) {
    if (item.dot || item.badge) return true;
    if (item.type === 'group' && item.buttons) {
      return item.buttons.some((btn) => btn.dot || btn.badge);
    }
    if (item.type === 'device-switcher' && item.devices) {
      return item.devices.some((dev) => dev.dot || dev.badge);
    }
    if (item.type === 'avatar-group' && item.avatars) {
      return item.avatars.some((av) => av.dot || av.badge);
    }
    return false;
  }

  _handleLogoClick() {
    this.onLogoClick();
  }

  _handleButtonClick(button, event) {
    if (button.disabled) return;
    if (button.onClick) {
      button.onClick(button, event);
      // Trigger update after button click to reflect any changes
      this.requestUpdate();
    }
  }

  _handleDeviceClick(deviceItem, device) {
    this.activeDevice = device.device;
    if (deviceItem.onDeviceChange) {
      deviceItem.onDeviceChange(device.device, device);
    }
    this.requestUpdate();
  }

  /**
   * Find and update button by id
   * @param {string} id - Button id
   * @param {object} props - Properties to update
   */
  updateButton(id, props) {
    const button = this._findButtonById(id);
    if (button) {
      Object.assign(button, props);
      this.requestUpdate();
    }
  }

  /**
   * Toggle button active state by id
   * @param {string} id - Button id
   */
  toggleButton(id) {
    const button = this._findButtonById(id);
    if (button) {
      button.active = !button.active;
      this.requestUpdate();
    }
  }

  /**
   * Set button active state by id
   * @param {string} id - Button id
   * @param {boolean} active - Active state
   */
  setButtonActive(id, active) {
    const button = this._findButtonById(id);
    if (button) {
      button.active = active;
      this.requestUpdate();
    }
  }

  /**
   * Find button by id (searches through all groups and top-level items)
   * @private
   */
  _findButtonById(id) {
    for (const item of this.items) {
      if (item.type === 'button' && item.id === id) {
        return item;
      }
      if (item.type === 'group' && item.buttons) {
        const button = item.buttons.find((btn) => btn.id === id);
        if (button) return button;
      }
    }
    return null;
  }

  _renderButton(button) {
    const variant = button.variant || '';
    const classes = {
      'toolbar-btn': !variant,
      [`action-btn ${variant}`]: variant,
      'tb-active': button.active,
      'tb-disabled': button.disabled,
    };
    const classList = Object.keys(classes)
      .filter((k) => classes[k])
      .join(' ');

    // Determine which icon to show: activeIcon if toggleIcon is true, otherwise icon
    const iconToShow = button.activeIcon && button.toggleIcon ? button.activeIcon : button.icon;

    // Apply custom size/padding if specified
    const inlineStyle = [];
    if (button.size) {
      const sizeMap = {
        small: 'min-width: 24px; height: 24px; padding: 4px 6px; font-size: 14px;',
        medium: 'min-width: 32px; height: 32px; padding: 6px 10px; font-size: 16px;',
        large: 'min-width: 40px; height: 40px; padding: 8px 14px; font-size: 18px;',
      };
      inlineStyle.push(sizeMap[button.size] || sizeMap.medium);
    }
    if (button.customStyle) {
      inlineStyle.push(button.customStyle);
    }

    return html`
      <button class="${classList}" @click=${(e) => this._handleButtonClick(button, e)} title="${button.tooltip || ''}" ?disabled=${button.disabled} style="${inlineStyle.join(' ')}">
        ${iconToShow ? unsafeHTML(iconToShow) : ''} ${button.label && !button.iconOnly ? html`<span class="btn-label">${button.label}</span>` : ''} ${button.badge ? html`<span class="sdvbadge">${button.badge}</span>` : ''}
        ${button.dot ? html`<span class="notification-dot" style="${button.dotColor ? `background-color: ${button.dotColor};` : ''}"></span>` : ''}
      </button>
    `;
  }

  _renderAvatar(avatar) {
    const size = avatar.size || 'medium'; // 'small', 'medium', 'large'
    const sizeMap = {
      small: '24px',
      medium: '32px',
      large: '40px',
    };
    const avatarSize = sizeMap[size] || sizeMap.medium;

    const classes = {
      'toolbar-avatar': true,
      'tb-clickable': avatar.onClick,
    };
    const classList = Object.keys(classes)
      .filter((k) => classes[k])
      .join(' ');

    // Generate initials from name if no src or text provided
    let displayText = avatar.text;
    if (!avatar.src && !displayText && avatar.name) {
      displayText = this._getInitials(avatar.name);
    }

    return html`
      <div class="${classList}" @click=${(e) => avatar.onClick && avatar.onClick(avatar, e)} title="${avatar.tooltip || avatar.name || ''}" style="width: ${avatarSize}; height: ${avatarSize}; ${avatar.customStyle || ''}">
        ${avatar.src ? html`<img src="${avatar.src}" alt="${avatar.alt || avatar.name || 'Avatar'}" class="avatar-img" />` : displayText ? html`<span class="avatar-text">${displayText}</span>` : ''} ${avatar.badge ? html`<span class="sdvbadge">${avatar.badge}</span>` : ''}
        ${avatar.dot ? html`<span class="notification-dot" style="${avatar.dotColor ? `background-color: ${avatar.dotColor};` : ''}"></span>` : ''}
      </div>
    `;
  }

  /**
   * Generate initials from a name
   * @param {string} name - Full name
   * @returns {string} - Initials (max 2 characters)
   * @private
   */
  _getInitials(name) {
    if (!name) return '';
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) {
      return parts[0].substring(0, 2).toUpperCase();
    }
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }

  _renderAvatarGroup(avatarGroup) {
    const max = avatarGroup.max || 3;
    const avatars = avatarGroup.avatars || [];
    const displayAvatars = avatars.slice(0, max);
    const remaining = avatars.length - max;

    const sizeMap = {
      small: '24px',
      medium: '32px',
      large: '40px',
    };
    const moreIconSize = sizeMap[avatarGroup.size] || '32px';
    //console.log('more size:', moreIconSize);
    return html`
      <div class="toolbar-avatar-group" style="${avatarGroup.customStyle || ''}">
        ${displayAvatars.map((avatar) => this._renderAvatar(avatar))}
        ${remaining > 0
          ? html`
              <div class="toolbar-avatar avatar-more" title="${remaining} more" style="width: ${moreIconSize}; height: ${moreIconSize};">
                <span class="avatar-text">+${remaining}</span>
              </div>
            `
          : ''}
      </div>
    `;
  }

  _renderGroup(group) {
    const style = group.style || 'normal'; // 'normal', 'boxed', 'pill'
    const align = group.align || 'left'; // 'left', 'center', 'right'

    const classes = {
      'toolbar-group': true,
      [`style-${style}`]: style !== 'normal',
      [`align-${align}`]: align !== 'left',
    };
    const classList = Object.keys(classes)
      .filter((k) => classes[k])
      .join(' ');

    // Apply custom gap if specified
    const inlineStyle = group.gap ? `gap: ${group.gap};` : '';

    return html`
      <div class="${classList}" style="${inlineStyle}; position: relative;">
        ${group.buttons && group.buttons.map((btn) => this._renderButton(btn))} ${group.dot ? html`<span class="notification-dot group-dot" style="${group.dotColor ? `background-color: ${group.dotColor};` : ''}"></span>` : ''}
      </div>
    `;
  }

  _renderDeviceSwitcher(item) {
    if (!item.devices || item.devices.length === 0) return '';
    return html`<rd-device-selector id="device_selector" class="device-selector"></rd-device-selector>`;
    return html`
      <div class="device-switcher" style="position: relative;">
        ${item.devices.map(
          (device) => html`
            <button class="device-btn ${device.device === this.activeDevice ? 'tb-active' : ''}" @click=${() => this._handleDeviceClick(item, device)} title="${device.tooltip || device.device}">
              ${device.icon ? unsafeHTML(device.icon) : ''} ${device.badge ? html`<span class="sdvbadge">${device.badge}</span>` : ''} ${device.dot ? html`<span class="notification-dot" style="${device.dotColor ? `background-color: ${device.dotColor};` : ''}"></span>` : ''}
            </button>
          `
        )}
        ${item.dot ? html`<span class="notification-dot group-dot" style="${item.dotColor ? `background-color: ${item.dotColor};` : ''}"></span>` : ''}
      </div>
    `;
  }

  _renderItem(item) {
    switch (item.type) {
      case 'button':
        return this._renderButton(item);

      case 'group':
        return this._renderGroup(item);

      case 'avatar':
        return this._renderAvatar(item);

      case 'avatar-group':
        return this._renderAvatarGroup(item);

      case 'device-switcher':
        return this._renderDeviceSwitcher(item);

      case 'divider':
        return html`<div class="tb-divider"></div>`;

      case 'spacer':
        return html`<div class="tb-spacer"></div>`;

      default:
        return '';
    }
  }
  _scrollLeft() {
    const container = this.querySelector('.toolbar-content');
    container.scrollBy({ left: -100, behavior: 'smooth' });
  }

  _scrollRight() {
    const container = this.querySelector('.toolbar-content');
    container.scrollBy({ left: 100, behavior: 'smooth' });
  }

  render() {
    return html`
      <style>
        ${unsafeCSS(styl)}
      </style>
      <div class="sadv-topbar">
        <!-- Logo -->
        ${this.logo
          ? html`
              <div class="logo-sect" @click=${this._handleLogoClick}>
                <img src="${this.logo}" alt="Logo" class="logo-image" />
              </div>
            `
          : ''}

        <!-- Toolbar Container - Flexible Items -->
        <div class="toolbar-container">
          <div class="lftCroller" style="display:none" @click=${this._scrollLeft}><i class="bx bx-chevron-left"></i></div>
          <div class="rgtCroller" style="display:none" @click=${this._scrollRight}><i class="bx bx-chevron-right"></i></div>
          <div class="toolbar-content">${this.items && this.items.map((item) => this._renderItem(item))}</div>
        </div>
      </div>
    `;
  }
}
if (!customElements.get('rd-topbar')) customElements.define('rd-topbar', TopBar);
